// Componente para mostrar la tarjeta del pedido rehusado seleccionado
// Displays the selected returned order card
import { Button } from '@shared/components/ui/button';
import { useTranslation } from 'react-i18next';
import type { OrderResponse } from '../../../adapters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingBag, faCalendar, faStore } from '@fortawesome/pro-light-svg-icons';

interface OrderCardProps {
  order: OrderResponse;
  productsCount: number;
  onProcessOrder?: () => void;
  children?: React.ReactNode;
  isProcessed?: boolean;
  isReceiving?: boolean;
}

export const OrderCard = ({
  order,
  productsCount,
  onProcessOrder,
  children,
  isProcessed = false,
  isReceiving = false,
}: OrderCardProps) => {
  const { t } = useTranslation('receiveModule');

  // Formatear fecha como DD-MM-YYYY HH:MM
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  return (
    <>
      {/* Título y botón fuera del contenedor - Solo cuando no es procesado */}
      {!isProcessed && (
        <div
          className={`flex items-center justify-between transition-opacity duration-300 ${
            isReceiving ? 'opacity-50 pointer-events-none' : 'opacity-100'
          }`}
        >
          <h2 className="text-title text-lg font-medium">
            {t('order.reused_title')} {order.ref}
          </h2>
          <Button
            variant="primary"
            size="lg"
            data-testid="process-order-button"
            onClick={onProcessOrder}
            disabled={isReceiving}
          >
            {t('buttons.process')}
          </Button>
        </div>
      )}

      {/* Contenedor principal */}
      <div
        className={`bg-card-bg border border-card-border rounded-lg p-4 transition-opacity duration-300 ${
          isReceiving ? 'opacity-50 pointer-events-none' : 'opacity-100'
        }`}
      >
        {/* Información del pedido dentro del contenedor */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faShoppingBag} className="w-3.5 h-3.5 text-icons" />
            <span className="font-medium text-title">{order.ref}</span>
          </div>
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faStore} className="w-3.5 h-3.5 text-icons" />
            <span className="text-xs">{order.shop?.name}</span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faCalendar} className="w-3.5 h-3.5 text-icons" />
            <span className="text-xs">{formatDate(order.order_created_at)}</span>
          </div>
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-bg-info text-info text-xs gap-2">
            <FontAwesomeIcon icon={faShoppingBag} className="w-3 h-3" />
            {productsCount} {t('order.products_label')}
          </span>
        </div>

        {/* Línea separadora - Solo cuando no es procesado */}
        {!isProcessed && <div className="my-4 border-t border-lines" />}

        {/* Contenido adicional (lista de productos) */}
        {children}
      </div>
    </>
  );
};
